import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, throwError } from "rxjs";
import { superAdminHttpRoutes, superAdminHttpVersion } from "../../../consts/super_admin_http_routes";
import { ApiService } from "../api.services";
import { portalHttpVersion } from "../../../consts/portal_http_routes";
import { EmployeesRequestSubscription } from "../../../models/employee.model";
import { isPlatformBrowser } from "@angular/common";
import moment from "moment";
import { localStorageConstants } from "../../../consts/local_storage_constants";
import { OrderRequestCompany } from "../../../models/order.model";


@Injectable({
    providedIn: 'root'
})
export class CompanyService {

    private url: string = superAdminHttpVersion.V1;
    private portalUrl: string = portalHttpVersion.V1;


    /**
    * BehaviorSubject used to share data (company ID) across components
    */
    private dataSource = new BehaviorSubject<string>('default');
    /**
     * Observable stream to subscribe and react to company ID changes
     */
    data$ = this.dataSource.asObservable();

    constructor(private apiService: ApiService, private _httpClient: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {
    }

    /**
     * Update the shared company ID state
     * @param value - New company ID to set
     */
    setData(value: string) {
        this.dataSource.next(value);
    }

    filter(q: string, page_number?: number, page_size?: number): Observable<any> {
        let params = new HttpParams();
        params = params.set('q', q);
        if (page_number) {
            params = params.set('page_number', page_number);
        }
        if (page_size) {
            params = params.set('page_size', page_size);
        }
        return this.apiService.httpGetCall(`${this.url}${superAdminHttpRoutes.COMPANY_GET_TABLE}`, params);
    }

    deactivate(companyId: string): Observable<any> {
        return this.apiService.httpPatchCall(`${this.url}company/${companyId}/status `, {
            "status": "Inactive"
        });
    }

    activate(companyId: string): Observable<any> {
        return this.apiService.httpPatchCall(`${this.url}company/${companyId}/status`, {
            "status": "Active"
        });
    }

    getDashboardCount(): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.url}${superAdminHttpRoutes.DASHBOARD_COUNT}`);
    }

    saveCompanyDetails(data: any) {
        return this.apiService.httpPostCall(`${this.url}company`, data);
    }

    saveOrderCompany(companyId: string, order: OrderRequestCompany) {
        return this.apiService.httpPostCall(`${this.url}order/company/${companyId}/products`, order);
    }

    uploadCompanyLogo(file: File, company_name: string): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("bucketName", "sos-superadmin-portal");
        formData.append("dirPath", `company/${company_name}/logo`);

        return this.apiService.httpPostCall<any>(
            `${this.url}${superAdminHttpRoutes.UPLOAD_PRODUCT_LOGO}`,
            formData
        );
    }


    getAllPlans(): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.url}plan/company-create`);
    }

    // Fetch Company by ID
    getCompanyById(id: string): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.url}company/${id}`);
    }

     getCompanyByIdForPortal(id: string): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.portalUrl}company/${id}`);
    }


    updateCompany(id: string, payload: any) {
        return this.apiService.httpPutCall(`${this.url}company/${id}`, payload);
    }

    //Portal dashboard Api
    getDashboardSubscriptions(companyId: string): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.portalUrl}subscription/${companyId}/dashboard/subscriptions`);
    }

    getDashboardSubscriptionsSummary(companyId: string, subscriptionId: string,): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.portalUrl}subscription/${companyId}/dashboard/subscriptions/${subscriptionId}/summary`);
    }

    getDashboardSubscriptionsEmployees(companyId: string, subscriptionId: string,): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.portalUrl}subscription/${companyId}/dashboard/subscriptions/${subscriptionId}/employees`);
    }

    getEmployeeCompany(companyId: string, q: string, sort_by?: string, page_number?: number, page_size?: number, productId?: any): Observable<any> {
        let params = new HttpParams();
        params = params.set('q', q);
        if (sort_by) {
            params = params.set('sort_by', sort_by);
        }
        if (page_number) {
            params = params.set('page_number', page_number);
        }
        if (page_size) {
            params = params.set('page_size', page_size);
        }
        if (companyId) {
            params = params.set('companyId', companyId);
        }
        if (productId) {
            params = params.set('productId', productId);
        }
        return this.apiService.httpGetCall(`${this.portalUrl}subscription/course-users/table/get`, params);
    }
    addEmployeeCompany(companyId: string, email: string): Observable<any> {
        return this._httpClient.post(`${this.portalUrl}/${companyId}/employees`, { email: email });
    }

    diactivateEmployee(companyId: string, email: string): Observable<any> {
        let params = new HttpParams();
        params = params.set('email', email);
        return this._httpClient.patch(`${this.url}/${companyId}/employees/deactivate`, null, { params });
    }

    activateEmployee(companyId: string, email: string): Observable<any> {
        let params = new HttpParams();
        params = params.set('email', email);
        return this._httpClient.patch(`${this.url}/${companyId}/employees/activate`, null, { params });
    }
    addSubscriptionsEmployee(companyId: string, employeesRequestSubscription: EmployeesRequestSubscription): Observable<any> {
        return this._httpClient.post(`${this.url}/${companyId}/employees`, employeesRequestSubscription);
    }

    // Get list of users for a company
    getCompanyUsers(companyId: string, productId: any): Observable<any> {
        return this.apiService.httpGetCall<any>(
            `${this.portalUrl}user/company-id/${companyId}/product-id/${productId}`,
            new HttpParams() // Add empty HttpParams if no query parameters are needed
        );
    }

    // Assign course to a user
    assignCourseToUser(companyId: string, body: { userId: string, subscriptionId: string }): Observable<any> {
        return this.apiService.httpPatchCall(
            `${this.portalUrl}subscription/assign-course/${companyId}`,
            body
        );
    }
    getPackageCourses(_packageId: string): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.url}${superAdminHttpRoutes.PLAN_ADD_EDIT}/${_packageId}/courses`)
    }


    getExcelReport(): Observable<ArrayBuffer> {
        let headers = new HttpHeaders();

        // Set language
        const language = isPlatformBrowser(this.platformId)
            ? localStorage.getItem(localStorageConstants.LANGUAGE) || 'en'
            : 'en';

        headers = headers.set('language', language);

        // Set local_offset
        headers = headers.set('local_offset', `${moment().utcOffset() * 60}`);

        // Set Authorization token
        if (isPlatformBrowser(this.platformId)) {
            const portalType = sessionStorage.getItem(localStorageConstants.USER_TYPE);
            const token = portalType === 'portal'
                ? localStorage.getItem(localStorageConstants.PORTAL_TOKEN) || ''
                : localStorage.getItem(localStorageConstants.SUPER_ADMIN_TOKEN) || '';

            headers = headers.set('Authorization', token);
        }

        // Make the request with headers and responseType
        return this._httpClient.get(`${this.portalUrl}subscription/report/get`, {
            headers,
            responseType: 'json'  // Change to json since we're getting a JSON response
        }).pipe(
            map((response: any) => {
                if (!response?.data?.data) {
                    throw new Error('Invalid response format');
                }
                return new Uint8Array(response.data.data).buffer;
            }),
            catchError((error) => {
                console.error('Download Excel report failed:', error);
                return throwError(() => error);
            })
        );
    }

    upgradeCompanyPackage(reqBody: any, companyId: any): Observable<any> {
        return this.apiService.httpPostCall(`${this.url}company/${companyId}/upgrade-package-pay`, reqBody);
    }

    cancelFreePlan(): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.portalUrl}company/cancel-free-plan`);
    }

    cancelFreePlanFromSuperadmin(companyId: any): Observable<any> {
        return this.apiService.httpGetCallWithoutParameter(`${this.url}company/${companyId}/cancel-free-plan`);
    }

    exportCompanies(): Observable<Blob> {
        return this._httpClient.get(`${this.url}company/export`, {
            headers: this.apiService.getHeaders(true),
            responseType: 'blob'
        });
    }
}
